import React, { useState, useEffect } from 'react';
import styles from './category6.module.scss';
import { Col, Row, Spin } from 'antd';
import { BusinessTravelData } from 'models/ActivityDataEntryScopes/businessTravel.model';
import ScopeThreeHeader from 'shared/components/Scope3Header';
import { ReactComponent as CategorySix } from 'assets/icons/flight_takeoff_cat_6.svg';
import { useParams } from 'react-router-dom';
import { category6, scope3 } from 'constants/constants';
import { ScopeThreeFormEditProvider } from 'context/ScopeThreeFormEditContext';
import { useScopeThreeCategoryUrlMode } from 'shared/hooks/useScopeThreeCategoryUrlMode';
import ActivityDataEntryService from 'services/ActivityDataEntryService';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useRedirect from 'shared/utils/useRedirect';
import { ActivityDataEntryScopeTabs } from 'enums/ScopeThreeCategoryStatusEnums';
import { useYearLabel } from 'shared/utils/generateYearSpan';
import { businessTravelValidationSchema } from './category6ValidationSchema';
import SharedAccordion from 'shared/components/Accordion';
import { Category6PanelComponent } from './category6PanelComponent';

const Category6 = () => {
  const { redirectToActivityDataEntry } = useRedirect();
  const { id: locationId } = useParams();
  const {
    adeParams,
    isEditView,
    enterEditMode,
    clearEditModeParams,
  } = useScopeThreeCategoryUrlMode();
  const { activeTab } = adeParams;
  const [activeTabKey, setActiveTabKey] = useState<string | string[]>('1');

  const {
    downloadScopeTemplate,
    loading,
    fetchCategory6,
    createCategory6ForLocation,
  } = ActivityDataEntryService();

  const methods = useForm<BusinessTravelData>({
    defaultValues: {
      distanceBasedTravel: [],
      spendBasedTravel: [],
      hotelStays: [],
    },
    resolver: yupResolver(businessTravelValidationSchema()),
    mode: 'onChange',
  });

  const { control, handleSubmit, reset, setValue } = methods;

  const { fields: distanceFields, append: appendDistance, remove: removeDistance } = useFieldArray({
    control,
    name: 'distanceBasedTravel',
  });

  const { fields: spendFields, append: appendSpend, remove: removeSpend } = useFieldArray({
    control,
    name: 'spendBasedTravel',
  });

  const { fields: hotelFields, append: appendHotel, remove: removeHotel } = useFieldArray({
    control,
    name: 'hotelStays',
  });

  // Transform data for frontend (similar to Category5 pattern):
  // 1. Ensure all arrays are present (even if empty)
  // 2. Preserve all fields from backend response
  // 3. Set default values for required fields
  const transformCategory6Data = (values?: BusinessTravelData): BusinessTravelData => {
    if (!values) {
      return {
        distanceBasedTravel: [],
        spendBasedTravel: [],
        hotelStays: [],
      };
    }

    return {
      distanceBasedTravel: (values.distanceBasedTravel || []).map(item => ({
        ...item,
        travelMode: item.travelMode || null,
        origin: item.origin || null,
        destination: item.destination || null,
        roundTrip: item.roundTrip ?? false,
        numberOfPassengers: item.numberOfPassengers ?? 1, // Default to 1 if null/undefined
        carSize: item.carSize || null,
        carType: item.carType || null,
        flightClass: item.flightClass || null,
      })),
      spendBasedTravel: (values.spendBasedTravel || []).map(item => ({
        ...item,
        spendType: item.spendType || null,
        money: item.money || null,
        moneyUnit: item.moneyUnit || null,
        spendLocation: item.spendLocation || null,
      })),
      hotelStays: (values.hotelStays || []).map(item => ({
        ...item,
        hotelNights: item.hotelNights || null,
        location: item.location || null,
      })),
    };
  };

  const loadCategory6Data = async () => {
    if (!locationId || !activeTab) return;
    try {
      const values = await fetchCategory6({
        locationId: locationId ?? '',
        year: activeTab,
      });
      const transformedValues = transformCategory6Data(values);
      if (transformedValues) {
        reset(transformedValues, {
          keepDefaultValues: false,
        });

        setTimeout(() => {
          if (
            transformedValues.distanceBasedTravel &&
            transformedValues.distanceBasedTravel.length > 0
          ) {
            transformedValues.distanceBasedTravel.forEach((item, index) => {
              if (item.travelMode) {
                setValue(
                  `distanceBasedTravel.${index}.travelMode`,
                  item.travelMode
                );
              }
              if (item.flightClass) {
                setValue(
                  `distanceBasedTravel.${index}.flightClass`,
                  item.flightClass
                );
              }
              if (item.carSize) {
                setValue(
                  `distanceBasedTravel.${index}.carSize`,
                  item.carSize
                );
              }
              if (item.carType) {
                setValue(
                  `distanceBasedTravel.${index}.carType`,
                  item.carType
                );
              }
              if (item.numberOfPassengers) {
                setValue(
                  `distanceBasedTravel.${index}.numberOfPassengers`,
                  item.numberOfPassengers
                );
              }
            });
          }
          if (
            transformedValues.spendBasedTravel &&
            transformedValues.spendBasedTravel.length > 0
          ) {
            transformedValues.spendBasedTravel.forEach((item, index) => {
              if (item.spendType) {
                setValue(
                  `spendBasedTravel.${index}.spendType`,
                  item.spendType
                );
              }
              if (item.moneyUnit) {
                setValue(
                  `spendBasedTravel.${index}.moneyUnit`,
                  item.moneyUnit
                );
              }
            });
          }
        }, 100);
      } else {
        reset({
          distanceBasedTravel: [],
          spendBasedTravel: [],
          hotelStays: [],
        });
      }
    } catch (error) {
      console.error('Error fetching Category 6 data:', error);
      reset({
        distanceBasedTravel: [],
        spendBasedTravel: [],
        hotelStays: [],
      });
    }
  };

  useEffect(() => {
    loadCategory6Data();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, activeTab]);

  const handleCancelEdit = async () => {
    await loadCategory6Data();
    clearEditModeParams();
  };

  const handleAccordionChange = (key: string | string[]) =>
    setActiveTabKey(key);

  const onSubmit = async (values: BusinessTravelData) => {
    // Filter out empty entries before submitting
    // Ensure all arrays are present (API requirement: arrays can be empty but must exist)
    const cleanedData: BusinessTravelData = {
      distanceBasedTravel: values?.distanceBasedTravel?.filter(
        item => item?.travelMode && item?.origin && item?.destination
      ) || [],
      spendBasedTravel: values?.spendBasedTravel?.filter(
        item => item?.spendType && item?.money && item?.spendLocation
      ) || [],
      hotelStays: values?.hotelStays?.filter(
        item => item?.location && item?.hotelNights
      ) || [],
    };

    const response = await createCategory6ForLocation(
      locationId,
      activeTab,
      category6,
      cleanedData
    );
    if (!response) return;
    redirectToActivityDataEntry(
      locationId,
      {
        activeTab,
        scopeTab: ActivityDataEntryScopeTabs.SCOPE_3_UPSTREAM,
      },
      true
    );
  };

  const downloadTemplate = async () => {
    if (!locationId) return;
    await downloadScopeTemplate(locationId, activeTab, scope3, category6);
  };

  const uploadCallBack = async () => {
    if (locationId && activeTab) {
      const updatedValues = await fetchCategory6({ locationId, year: activeTab });
      const transformedValues = transformCategory6Data(updatedValues);
      methods.reset(transformedValues, { keepDefaultValues: false });

      // Explicitly set values after reset to ensure Select components recognize them
      // This is consistent with how loadData handles the form reset
      setTimeout(() => {
        if (transformedValues.distanceBasedTravel && transformedValues.distanceBasedTravel.length > 0) {
          transformedValues.distanceBasedTravel.forEach((item, index) => {
            if (item.travelMode) {
              setValue(`distanceBasedTravel.${index}.travelMode`, item.travelMode);
            }
            if (item.flightClass) {
              setValue(`distanceBasedTravel.${index}.flightClass`, item.flightClass);
            }
            if (item.carSize) {
              setValue(`distanceBasedTravel.${index}.carSize`, item.carSize);
            }
            if (item.carType) {
              setValue(`distanceBasedTravel.${index}.carType`, item.carType);
            }
            if (item.numberOfPassengers) {
              setValue(`distanceBasedTravel.${index}.numberOfPassengers`, item.numberOfPassengers);
            }
          });
        }
        if (transformedValues.spendBasedTravel && transformedValues.spendBasedTravel.length > 0) {
          transformedValues.spendBasedTravel.forEach((item, index) => {
            if (item.spendType) {
              setValue(`spendBasedTravel.${index}.spendType`, item.spendType);
            }
            if (item.moneyUnit) {
              setValue(`spendBasedTravel.${index}.moneyUnit`, item.moneyUnit);
            }
          });
        }
      }, 100);
    }
  };

  return (
    <ScopeThreeFormEditProvider isEditView={isEditView}>
      <div className={styles.pageWrapper}>
        <Spin spinning={loading} className={styles.loader}>
          <FormProvider {...methods}>
            <Row className={styles.contentContainer}>
              <Col span={24} className={styles.contentWrapper}>
                <ScopeThreeHeader
                  icon={<CategorySix />}
                  title="Category 6 - Business Travel"
                  year={useYearLabel(activeTab)}
                  onSubmit={handleSubmit(onSubmit)}
                  isUploadExist
                  downloadTemplate={downloadTemplate}
                  uploadCallBack={uploadCallBack}
                  loading={loading}
                  category={category6}
                  isEditView={isEditView}
                  onEnterEdit={enterEditMode}
                  onCancelEdit={handleCancelEdit}
                />
              </Col>
            <Col span={24} className={styles.contentWrapper}>
              <div style={{ padding: '1rem 2rem', color: '#666', fontSize: '0.875rem' }}>
                Note:  Emissions from employee travel for business purposes via air, rail, road, or other modes in vehicles not owned by the company.
              </div>
            </Col>
            <Col span={24} className={styles.accordionWrapper}>
              <SharedAccordion
                accordion={true}
                expandIconPosition="end"
                activeKey={activeTabKey}
                onChange={handleAccordionChange}
                panels={Category6PanelComponent({
                  distanceFields,
                  appendDistance,
                  removeDistance,
                  spendFields,
                  appendSpend,
                  removeSpend,
                  hotelFields,
                  appendHotel,
                  removeHotel,
                  setActiveTabKey,
                  isEditView,
                })}
              />
            </Col>
          </Row>
        </FormProvider>
      </Spin>
      </div>
    </ScopeThreeFormEditProvider>
  );
};

export default Category6;
